import React, { memo, ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, Button, Space, DatePicker } from 'antd';
import moment from 'moment';

import { TCalendarRow, useReschedule } from '../shared';

export interface IReschedulePopoverProps {
  /** The row to move. */
  row: TCalendarRow;
  /** Called after a successful reschedule (e.g. refetch the calendar). */
  onMoved?: () => void;
  /** Controlled visibility (optional) — lets a menu open the popover. */
  visible?: boolean;
  /** Controlled visibility change handler (pairs with `visible`). */
  onVisibleChange?: (visible: boolean) => void;
  /** The trigger element the popover wraps. */
  children: ReactNode;
}

const DATE_FMT = 'YYYY-MM-DD';

/**
 * Wraps a trigger with a Popover offering today / +1 day / +1 week / pick-date,
 * each routed through the shared `useReschedule` action. Uncontrolled by
 * default (opens on trigger click); pass `visible`/`onVisibleChange` to drive
 * it from outside (e.g. the row ⋯-menu's «Перенести» item).
 */
export const ReschedulePopover = memo<IReschedulePopoverProps>(
  ({ row, onMoved, visible: controlledVisible, onVisibleChange, children }) => {
    const { t } = useTranslation();
    const [innerVisible, setInnerVisible] = useState(false);
    const isControlled = controlledVisible !== undefined;
    const visible = isControlled ? controlledVisible : innerVisible;

    const setVisible = useCallback(
      (next: boolean) => {
        if (!isControlled) setInnerVisible(next);
        onVisibleChange?.(next);
      },
      [isControlled, onVisibleChange],
    );

    const { reschedule, loading } = useReschedule({ onDone: onMoved });

    const move = useCallback(
      async (iso: string) => {
        await reschedule(row, iso);
        setVisible(false);
      },
      [reschedule, row, setVisible],
    );

    const content = (
      <Space direction="vertical" size="small">
        <Button
          size="small"
          block
          loading={loading}
          onClick={() => move(moment().format(DATE_FMT))}
        >
          {t('pages.paymentCalendar.reschedule.today')}
        </Button>
        <Button
          size="small"
          block
          loading={loading}
          onClick={() =>
            move(
              moment(row.date || undefined)
                .add(1, 'day')
                .format(DATE_FMT),
            )
          }
        >
          {t('pages.paymentCalendar.reschedule.plusDay')}
        </Button>
        <Button
          size="small"
          block
          loading={loading}
          onClick={() =>
            move(
              moment(row.date || undefined)
                .add(1, 'week')
                .format(DATE_FMT),
            )
          }
        >
          {t('pages.paymentCalendar.reschedule.plusWeek')}
        </Button>
        <DatePicker
          size="small"
          allowClear={false}
          disabled={loading}
          placeholder={t('pages.paymentCalendar.reschedule.pickDate')}
          onChange={(d) => {
            if (d) move(d.format(DATE_FMT));
          }}
        />
      </Space>
    );

    return (
      <Popover
        trigger="click"
        visible={visible}
        onVisibleChange={setVisible}
        title={t('pages.paymentCalendar.reschedule.title')}
        content={content}
      >
        {children}
      </Popover>
    );
  },
);
